import { Crown, ArrowUpRight, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { fmtUSD, entityImage, entityLabel, entityName } from "@/lib/format";
import { jumpLabel, passPhrase } from "@/components/leaderboard/climbTargets";

export function BoostConfirmationCopy({
  entity,
  target,
  priceChanged = false,
  freshAmount = null,
  revalidating = false,
  error = null,
  onConfirm,
  onContinue,
  onCancel,
}) {
  const label = entityLabel(entity);
  const currentRank = entity.leaderboard_rank || entity.rank;
  const shownAmount = priceChanged ? freshAmount : target.amount_usd;
  const passed = passPhrase(target);
  const passedCount = (target.overtaken || []).length;
  const image = entityImage(entity);

  return (
    <div data-testid="boost-confirmation" className="relative">
      <div className="flex items-center gap-3">
        {image ? (
          <img src={image} alt="" className="h-11 w-11 rounded-full border border-cyan-200/70 object-cover" />
        ) : (
          <div className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-200/70 bg-cyan-400/15 font-display text-sm font-black text-cyan-50">
            {label.slice(0, 2)}
          </div>
        )}
        <div>
          <div className="font-display text-sm font-black tracking-tight">{label}</div>
          <div className="text-xs text-zinc-500">{entityName(entity)}</div>
        </div>
      </div>
      {!priceChanged && (
        <div className="climb-backed-card pointer-events-none absolute right-8 top-0 hidden flex-col px-3 py-2 sm:flex" aria-hidden="true">
          <span className="text-lg leading-none">+{fmtUSD(target.amount_usd)}</span>
          <span className="mt-0.5 text-[10px] tracking-[0.18em]">BACKED</span>
        </div>
      )}
      {priceChanged ? (
        <div className="mt-5 rounded-xl border border-cyan-300/30 bg-cyan-400/[0.06] px-4 py-3" role="status">
          <div className="text-sm font-bold text-white">Rank price changed</div>
          <p className="mt-1 font-display text-lg font-black text-cyan-100" data-testid="price-changed-copy">
            Take #{target.target_rank} now costs {fmtUSD(freshAmount)}
          </p>
        </div>
      ) : (
        <h2 className="mt-5 font-display text-3xl font-black tracking-tight" data-testid="confirm-price-copy">
          Take #{target.target_rank} for {fmtUSD(target.amount_usd)}
        </h2>
      )}
      <div className="mt-4 space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-sm">
        <div className="flex gap-3">
          <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-cyan-200" />
          <div>
            <div className="font-bold text-white">{jumpLabel(target.positions_gained)}</div>
            <div className="text-zinc-400">From #{currentRank} to #{target.target_rank}</div>
          </div>
        </div>
        {passed && (
          <div className="flex gap-3">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-cyan-200" />
            <div>
              <div data-testid="passed-tokens">Pass {passed}</div>
              <div className="text-zinc-400">Overtake {passedCount} {passedCount === 1 ? "token" : "tokens"}</div>
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <Crown className="mt-0.5 h-4 w-4 shrink-0 text-cyan-200" />
          <div>
            <div className="font-bold text-white">New rank after payment: #{target.target_rank}</div>
            <div className="text-zinc-400">{label} will be in position #{target.target_rank}</div>
          </div>
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-negative" data-testid="boost-confirm-error">{error}</p>
      )}
      <div className="mt-5 flex flex-col gap-2">
        {priceChanged ? (
          <button
            type="button"
            data-testid="continue-changed-price"
            disabled={revalidating || shownAmount == null}
            onClick={onContinue}
            className="rounded-full bg-cyan-200 px-4 py-3 text-sm font-black text-black hover:bg-cyan-100 disabled:opacity-50"
          >
            Continue for {fmtUSD(freshAmount)}
          </button>
        ) : (
          <button
            type="button"
            data-testid="confirm-boost"
            disabled={revalidating}
            onClick={onConfirm}
            className="rounded-full bg-cyan-200 px-4 py-3 text-sm font-black text-black hover:bg-cyan-100 disabled:opacity-50"
          >
            {revalidating ? "Checking latest price…" : "Confirm boost"}
          </button>
        )}
        <button
          type="button"
          data-testid="cancel-boost"
          onClick={onCancel}
          className="rounded-full border border-white/15 px-4 py-2.5 text-sm font-bold text-zinc-300 hover:bg-white/5"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function BoostConfirmation({ mobile, open = true, onCancel, ...copyProps }) {
  const label = entityLabel(copyProps.entity);
  if (mobile) {
    return (
      <Drawer open={open} shouldScaleBackground={false} onOpenChange={(next) => { if (!next) onCancel(); }}>
        <DrawerContent className="border-white/10 bg-[#0a0a0a] px-5 pb-8 text-white">
          <DrawerTitle className="sr-only">Confirm boost for {label}</DrawerTitle>
          <DrawerDescription className="sr-only">
            Review the rank and price before opening payment.
          </DrawerDescription>
          <div className="pt-2">
            <BoostConfirmationCopy {...copyProps} onCancel={onCancel} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-md border-cyan-300/40 bg-[#070b10] text-white shadow-[0_0_40px_rgba(34,211,238,0.28)]">
        <DialogTitle className="sr-only">Confirm boost for {label}</DialogTitle>
        <DialogDescription className="sr-only">
          Review the rank and price before opening payment.
        </DialogDescription>
        <BoostConfirmationCopy {...copyProps} onCancel={onCancel} />
      </DialogContent>
    </Dialog>
  );
}
